<template>
  <div class="gauge-module">
    <div class="module-title">设备关联率</div>
    <div class="gauge-container">
      <div 
        v-for="machine in sortedMachines" 
        :key="machine.machineCode"
        class="gauge-item"
        :class="getBorderColorClass(machine.associationRate)"
      >
        <div :ref="`gauge-${machine.machineCode}`" class="gauge-chart"></div>
        <div class="gauge-label">{{ machine.machineName }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'GaugeModule',
  props: {
    machines: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      chartInstances: {}
    }
  },
  computed: {
    sortedMachines() {
      // 按关联级别排序: 1=父级, 2=子级
      return [...this.machines].sort((a, b) => {
        return a.associationLevel - b.associationLevel
      })
    }
  },
  watch: {
    sortedMachines: {
      handler(newMachines) {
        this.$nextTick(() => {
          // 检查是否需要重新初始化（机器数量或机器代码变化）
          const needReinit = this.checkNeedReinit(newMachines)
          
          if (needReinit) {
            this.initCharts()
          } else {
            // 只更新数据，不重新创建图表
            this.updateCharts()
          }
        })
      },
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initCharts()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    this.destroyCharts()
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    checkNeedReinit(newMachines) {
      // 检查机器数量是否变化
      const currentCodes = Object.keys(this.chartInstances)
      const newCodes = newMachines.map(m => m.machineCode)
      
      if (currentCodes.length !== newCodes.length) {
        return true
      }
      
      // 检查机器代码是否变化
      for (const code of newCodes) {
        if (!currentCodes.includes(code)) {
          return true
        }
      }
      
      return false
    },
    
    updateCharts() {
      // 只更新图表数据，不重新创建
      this.sortedMachines.forEach(machine => {
        const chart = this.chartInstances[machine.machineCode]
        if (!chart) return
        
        const value = machine.associationRate || 0
        
        // 根据关联率确定颜色
        let valueColor = '#00ffff'
        if (value >= 90) {
          valueColor = '#00ff00'
        } else if (value >= 80) {
          valueColor = '#ffff00'
        } else {
          valueColor = '#ff0000'
        }
        
        // 只更新数据和颜色，不触发指针动画
        chart.setOption({
          series: [{
            pointer: {
              itemStyle: {
                color: valueColor,
                shadowColor: `${valueColor}cc`,
                shadowBlur: 8
              }
            },
            detail: {
              valueAnimation: false, // 关闭值动画
              formatter: function(value) {
                return '{value|' + value.toFixed(1) + '}{unit|%}'
              },
              rich: {
                value: {
                  fontSize: 26,
                  fontWeight: 'bold',
                  color: valueColor,
                  textShadowColor: `${valueColor}cc`,
                  textShadowBlur: 10
                },
                unit: {
                  fontSize: 18,
                  color: valueColor,
                  padding: [0, 0, 0, 4]
                }
              }
            },
            data: [{
              value: value,
              name: ''
            }]
          }]
        }, false) // 第二个参数false表示不合并，直接替换
      })
    },
    
    getBorderColorClass(rate) {
      const value = rate || 0
      if (value >= 90) return 'border-green'    // 绿色边框：≥90%
      if (value >= 80) return 'border-yellow'   // 黄色边框：80-89%
      return 'border-red'                        // 红色边框：<80%
    },
    
    handleResize() {
      // 重新调整所有图表大小
      Object.values(this.chartInstances).forEach(chart => {
        if (chart) {
          chart.resize()
        }
      })
    },
    
    initCharts() {
      this.sortedMachines.forEach(machine => {
        const refName = `gauge-${machine.machineCode}`
        const dom = this.$refs[refName]
        
        if (!dom || !dom[0]) return
        
        // 销毁旧实例
        if (this.chartInstances[machine.machineCode]) {
          this.chartInstances[machine.machineCode].dispose()
        }
        
        // 创建新实例
        const chart = echarts.init(dom[0])
        this.chartInstances[machine.machineCode] = chart
        
        // 配置仪表盘
        const option = this.getGaugeOption(machine)
        chart.setOption(option)
      })
    },
    
    getGaugeOption(machine) {
      const value = machine.associationRate || 0
      
      // 根据关联率确定颜色
      let valueColor = '#00ffff'  // 默认青色
      if (value >= 90) {
        valueColor = '#00ff00'    // 绿色：≥90%
      } else if (value >= 80) {
        valueColor = '#ffff00'    // 黄色：80-89%
      } else {
        valueColor = '#ff0000'    // 红色：<80%
      }
      
      return {
        series: [{
          type: 'gauge',
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: 100,
          splitNumber: 10,
          radius: '75%',
          center: ['50%', '55%'],
          axisLine: {
            lineStyle: {
              width: 10,
              color: [
                [0.8, '#ff0000'],   // 0-80%: 红色
                [0.9, '#ffff00'],   // 80-90%: 黄色
                [1, '#00ff00']      // 90-100%: 绿色
              ],
              shadowColor: 'rgba(0, 255, 255, 0.3)',
              shadowBlur: 8
            }
          },
          progress: {
            show: false
          },
          pointer: {
            itemStyle: {
              color: valueColor,
              shadowColor: `${valueColor}cc`,
              shadowBlur: 8
            },
            width: 4,
            length: '60%'
          },
          axisTick: {
            distance: -12,
            length: 5,
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.8)',
              width: 1.5
            },
            splitNumber: 5
          },
          splitLine: {
            distance: -15,
            length: 10,
            lineStyle: {
              color: '#ffffff',
              width: 2
            }
          },
          axisLabel: {
            color: '#ffffff',
            distance: 10,
            fontSize: 12,
            fontWeight: 'bold',
            fontFamily: 'Arial, sans-serif',
            formatter: function(value) {
              return value
            }
          },
          title: {
            show: false
          },
          detail: {
            valueAnimation: false, // 关闭初始值动画
            formatter: function(value) {
              return '{value|' + value.toFixed(1) + '}{unit|%}'
            },
            rich: {
              value: {
                fontSize: 26,
                fontWeight: 'bold',
                color: valueColor,
                textShadowColor: `${valueColor}cc`,
                textShadowBlur: 10
              },
              unit: {
                fontSize: 18,
                color: valueColor,
                padding: [0, 0, 0, 4]
              }
            },
            offsetCenter: [0, '75%']
          },
          data: [{
            value: value,
            name: ''
          }]
        }]
      }
    },
    
    destroyCharts() {
      Object.values(this.chartInstances).forEach(chart => {
        if (chart) {
          chart.dispose()
        }
      })
      this.chartInstances = {}
    }
  }
}
</script>

<style lang="scss" scoped>
.gauge-module {
  background: rgba(10, 20, 40, 0.6);
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 8px;
  padding: 15px;
  backdrop-filter: blur(10px);
  box-shadow: 0 0 20px rgba(0, 255, 255, 0.2);
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.module-title {
  color: #00ffff;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
  text-align: center;
  flex-shrink: 0;
}

.gauge-container {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 15px;
  padding: 5px;
  align-items: center;
  overflow: hidden;
}

.gauge-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 8px;
  border: 2px solid rgba(0, 255, 255, 0.15);
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-3px);
  }
  
  &.border-green {
    border-color: rgba(0, 255, 0, 0.7);
    box-shadow: 0 0 20px rgba(0, 255, 0, 0.3);
    
    &:hover {
      box-shadow: 0 0 30px rgba(0, 255, 0, 0.5);
    }
  }
  
  &.border-yellow {
    border-color: rgba(255, 255, 0, 0.7);
    box-shadow: 0 0 20px rgba(255, 255, 0, 0.3);
    
    &:hover {
      box-shadow: 0 0 30px rgba(255, 255, 0, 0.5);
    }
  }
  
  &.border-red {
    border-color: rgba(255, 0, 0, 0.7);
    box-shadow: 0 0 20px rgba(255, 0, 0, 0.3);
    
    &:hover {
      box-shadow: 0 0 30px rgba(255, 0, 0, 0.5);
    }
  }
}

.gauge-label {
  color: #00ffff;
  font-size: 16px;
  font-weight: bold;
  margin-top: 5px;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.6);
  letter-spacing: 1px;
  text-align: center;
}

.gauge-chart {
  width: 100%;
  height: 180px;
  flex-shrink: 0;
}
</style>
